import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, keepPreviousData, useQueryClient } from "@tanstack/react-query";
import { Plus, Search } from "lucide-react";
import { PageHeader } from "../components/shared/PageHeader";
import { DataTable } from "../components/shared/DataTable";
import { Pagination } from "../components/shared/Pagination";
import { StatusBadge } from "../components/shared/StatusBadge";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";
import { Button } from "../components/ui/button";
import { CustomerFormDialog } from "../components/customers/CustomerFormDialog";
import { SortControl } from "../components/shared/SortControl";
import { apiClient, getApiErrorMessage } from "../lib/apiClient";
import { useDebouncedValue } from "../hooks/useDebouncedValue";
import { useListState } from "../hooks/useListState";
import { customerStatusMeta } from "../lib/statusMaps";

const PAGE_SIZE = 20;
const SORT_FIELDS = [{ value: "id", label: "Date Added" }];

export default function Customers() {
  const navigate = useNavigate();
  const [search, setSearch] = useListState("customers.search", "");
  const debouncedSearch = useDebouncedValue(search, 400);
  const [includeDeleted, setIncludeDeleted] = useListState("customers.includeDeleted", false);
  const [geoMissing, setGeoMissing] = useListState("customers.geoMissing", false);
  const [sortDir, setSortDir] = useListState("customers.sortDir", "desc");
  const [page, setPage] = useListState("customers.page", 0);
  const [formOpen, setFormOpen] = useState(false);
  const queryClient = useQueryClient();

  const listQuery = useQuery({
    queryKey: ["customers-list", { q: debouncedSearch, includeDeleted, geoMissing, sortDir, page }],
    queryFn: async () => {
      const { data } = await apiClient.get("/customers", {
        params: {
          q: debouncedSearch || undefined,
          include_deleted: includeDeleted,
          geo_missing: geoMissing || undefined,
          sort_dir: sortDir,
          limit: PAGE_SIZE,
          offset: page * PAGE_SIZE,
        },
      });
      return data;
    },
    placeholderData: keepPreviousData,
  });

  const items = listQuery.data?.items ?? [];
  const total = listQuery.data?.total ?? 0;
  const error = listQuery.isError ? getApiErrorMessage(listQuery.error, "Could not load customers.") : null;

  // Reset to the first page whenever a filter/search changes (the query reacts
  // to the key change on its own — no manual refetch needed).
  const filterKey = `${debouncedSearch}|${includeDeleted}|${geoMissing}|${sortDir}`;
  const prevFilterKeyRef = useRef(filterKey);
  useEffect(() => {
    if (prevFilterKeyRef.current !== filterKey) {
      prevFilterKeyRef.current = filterKey;
      if (page !== 0) setPage(0);
    }
  }, [filterKey, page, setPage]);

  const handleCreated = () => {
    setFormOpen(false);
    queryClient.invalidateQueries({ queryKey: ["customers-list"] });
  };

  const columns = [
    { key: "id", header: "ID", className: "text-text-muted" },
    {
      key: "name", header: "Name",
      render: (r) => (
        <span className="font-medium text-text-main">
          {`${r.firstname || ""} ${r.lastname || ""}`.trim() || "—"}
        </span>
      ),
    },
    { key: "email", header: "Email", subtitle: true, render: (r) => r.email || "—" },
    { key: "phone", header: "Phone" },
    { key: "location", header: "Location", render: (r) => [r.city, r.state].filter(Boolean).join(", ") || "—" },
    {
      key: "status", header: "Status",
      render: (r) => {
        const meta = customerStatusMeta(r);
        return <StatusBadge label={meta.label} variant={meta.variant} />;
      },
    },
  ];

  return (
    <div data-testid="customers-page">
      <PageHeader
        title="Customers"
        description="Search, manage and review every customer on file."
        actions={
          <Button variant="accent" onClick={() => setFormOpen(true)} data-testid="customer-create-button">
            <Plus size={16} strokeWidth={2} /> New Customer
          </Button>
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-4">
        <div className="relative w-full max-w-xs">
          <Search size={15} strokeWidth={1.5} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
          <Input
            className="pl-9"
            placeholder="Search name, phone, address..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="customer-search-input"
          />
        </div>
        <label className="flex cursor-pointer items-center gap-2 text-sm text-text-muted" data-testid="customer-show-deleted-toggle">
          <Checkbox
            checked={includeDeleted}
            onCheckedChange={(v) => setIncludeDeleted(Boolean(v))}
            data-testid="customer-show-deleted-checkbox"
          />
          Show deleted customers
        </label>
        <label className="flex cursor-pointer items-center gap-2 text-sm text-text-muted" data-testid="customer-geo-missing-toggle">
          <Checkbox
            checked={geoMissing}
            onCheckedChange={(v) => setGeoMissing(Boolean(v))}
            data-testid="customer-geo-missing-checkbox"
          />
          Location missing
        </label>
        <SortControl
          fields={SORT_FIELDS} sortBy="id" onSortByChange={() => {}} sortDir={sortDir} onSortDirChange={setSortDir}
          testId="customer-sort"
        />
      </div>

      <DataTable
        testId="customers-table"
        columns={columns}
        rows={items}
        loading={listQuery.isPending}
        error={error}
        onRetry={() => listQuery.refetch()}
        onRowClick={(row) => navigate(`/customers/${row.id}`)}
        emptyTitle="No customers found"
        emptyDescription="Try adjusting your search, or add a new customer to get started."
      />
      <Pagination
        page={page}
        pageSize={PAGE_SIZE}
        total={total}
        onPageChange={setPage}
        testId="customers-pagination"
      />

      <CustomerFormDialog open={formOpen} onOpenChange={setFormOpen} onSaved={handleCreated} />
    </div>
  );
}
